import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { commentsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const CommentsSection = ({ recipeId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!recipeId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await commentsAPI.list(recipeId);
        if (!cancelled) setComments(res.data);
      } catch {
        if (!cancelled) toast.error('Erreur de chargement des commentaires');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour commenter');
      return;
    }
    const content = newContent.trim();
    if (!content) return;
    setSubmitting(true);
    try {
      const res = await commentsAPI.create(recipeId, content);
      setComments((prev) => [res.data, ...prev]);
      setNewContent('');
      toast.success('Commentaire publié');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentsAPI.remove(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success('Commentaire supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const initials = (name) =>
    (name || '?')
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  return (
    <div className="bg-worldplate-paper rounded-2xl p-6 border border-worldplate-border" data-testid="comments-section">
      <h2 className="font-heading text-2xl font-bold text-worldplate-deep mb-6 flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-worldplate-primary" />
        Commentaires
        {!loading && comments.length > 0 && (
          <span className="text-base font-normal text-worldplate-muted">({comments.length})</span>
        )}
      </h2>

      {/* Compose */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-worldplate-secondary text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {initials(user?.username)}
            </div>
            <div className="flex-1">
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Partagez votre avis, vos astuces, vos variantes…"
                className="min-h-[80px] resize-none bg-worldplate-subtle border-worldplate-border focus-visible:ring-worldplate-primary"
                maxLength={1000}
                data-testid="comment-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-worldplate-muted">{newContent.length}/1000</span>
                <Button
                  type="submit"
                  disabled={submitting || !newContent.trim()}
                  className="bg-worldplate-primary hover:bg-worldplate-primary/90 gap-2"
                  data-testid="comment-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Publier
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 rounded-xl bg-worldplate-subtle text-sm text-worldplate-muted">
          <Link to="/login" className="text-worldplate-primary hover:underline font-medium">
            Connectez-vous
          </Link>{' '}
          pour laisser un commentaire.
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-20 bg-worldplate-subtle rounded-xl animate-pulse" />
          ))}
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-worldplate-muted">
          <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Soyez le premier à commenter !</p>
        </div>
      ) : (
        <ul className="space-y-4">
          <AnimatePresence initial={false}>
            {comments.map((c) => (
              <motion.li
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex gap-3 group"
                data-testid={`comment-${c.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-worldplate-deep text-worldplate-cream flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {initials(c.user_name)}
                </div>
                <div className="flex-1 min-w-0 bg-worldplate-subtle rounded-2xl p-4 relative">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="font-semibold text-worldplate-deep truncate">{c.user_name}</span>
                      <span className="text-xs text-worldplate-muted">{formatDate(c.created_at)}</span>
                    </div>
                    {user?.id === c.user_id && (
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="text-worldplate-muted hover:text-worldplate-error opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Supprimer"
                        data-testid={`comment-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-worldplate-text whitespace-pre-wrap break-words">{c.content}</p>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
};

export default CommentsSection;
